import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as schemas from '../../../assets/schemas/schemas';
import { ActionButton } from '../../common/Buttons/Buttons';
import {
  GeographicStatesTable,
  GeographicCitiesTable,
  GeographicMunicipalitiesTable,
} from '../../common/Tables/Tables.jsx';
import {
  geographicStatesTableHeader,
  geographicCitiesTableHeader,
  geographicMunicipalitiesTableHeader,
} from '../../../assets/helpers/tables.js';
import { capitalize } from '../../../assets/helpers/funciones';
import { notify } from '../../../assets/helpers/toast';
import { statesService } from '../../../services/statesService.js';
import { itemsPerPageOptions, initialPager } from './parametersConstants.js';
import { buildTableState, inactiveRowCount } from './parametersTableUtils.js';
import { GeographicStateFormPanel } from './GeographicStateFormPanel.jsx';
import { GeographicCityFormPanel } from './GeographicCityFormPanel.jsx';
import { GeographicMunicipalityFormPanel } from './GeographicMunicipalityFormPanel.jsx';

export const GeographicParametersSection = () => {
  const [geoSubTab, setGeoSubTab] = useState('estados');

  const [statesPager, setStatesPager] = useState(initialPager);

  const [statesList, setStatesList] = useState([]);
  const [statesForDropdown, setStatesForDropdown] = useState([]);
  const [statesListLoading, setStatesListLoading] = useState(false);
  const [statesListVersion, setStatesListVersion] = useState(0);

  const [citiesData, setCitiesData] = useState({
    limit: initialPager.limit,
    page: 1,
    data: [],
    count: 0,
    isLoading: false,
  });
  const [citiesListVersion, setCitiesListVersion] = useState(0);

  const [municipalitiesData, setMunicipalitiesData] = useState({
    limit: initialPager.limit,
    page: 1,
    data: [],
    count: 0,
    isLoading: false,
  });
  const [municipalitiesListVersion, setMunicipalitiesListVersion] =
    useState(0);

  const [citiesForMunicipalitySelect, setCitiesForMunicipalitySelect] =
    useState([]);
  const municipalityStateIdPrevRef = useRef(undefined);

  // — Estados tab filter —
  const [filterStatesTabIds, setFilterStatesTabIds] = useState([]);

  // — Ciudades tab filters —
  const [filterCitiesStateIds, setFilterCitiesStateIds] = useState([]);
  const [filterCitiesIds, setFilterCitiesIds] = useState([]);
  const [cityFilterOptions, setCityFilterOptions] = useState([]);
  const [, setCityFilterOptionsLoading] = useState(false);

  // — Municipios tab filters —
  const [filterMunicipStateIds, setFilterMunicipStateIds] = useState([]);
  const [filterMunicipCityIds, setFilterMunicipCityIds] = useState([]);
  const [filterMunicipIds, setFilterMunicipIds] = useState([]);
  const [cityOptionsForMunicipFilter, setCityOptionsForMunicipFilter] = useState([]);
  const [, setCityOptionsForMunicipFilterLoading] = useState(false);
  const [municipalityFilterOptions, setMunicipalityFilterOptions] = useState([]);
  const [, setMunicipalityFilterOptionsLoading] = useState(false);

  const [editingStateId, setEditingStateId] = useState(null);
  const [showStateForm, setShowStateForm] = useState(false);

  const [editingCityId, setEditingCityId] = useState(null);
  const [showCityForm, setShowCityForm] = useState(false);

  const [editingMunicipalityId, setEditingMunicipalityId] = useState(null);
  const [showMunicipalityForm, setShowMunicipalityForm] = useState(false);

  const stateValidationSchema = useMemo(
    () => schemas.createGeographicStateSchema(),
    []
  );

  const {
    register: registerState,
    handleSubmit: handleSubmitState,
    reset: resetStateForm,
    formState: { errors: stateErrors },
  } = useForm({
    resolver: yupResolver(stateValidationSchema),
    defaultValues: {
      name: '',
    },
  });

  const cityValidationSchema = useMemo(
    () => schemas.createGeographicCitySchema(!!editingCityId),
    [editingCityId]
  );

  const {
    register: registerCity,
    handleSubmit: handleSubmitCity,
    reset: resetCityForm,
    formState: { errors: cityErrors },
  } = useForm({
    resolver: yupResolver(cityValidationSchema),
    defaultValues: {
      stateId: '',
      cityName: '',
    },
  });

  const municipalityValidationSchema = useMemo(
    () => schemas.createGeographicMunicipalitySchema(!!editingMunicipalityId),
    [editingMunicipalityId]
  );

  const {
    register: registerMunicipality,
    handleSubmit: handleSubmitMunicipality,
    reset: resetMunicipalityForm,
    watch: watchMunicipality,
    setValue: setMunicipalityValue,
    formState: { errors: municipalityErrors },
  } = useForm({
    resolver: yupResolver(municipalityValidationSchema),
    defaultValues: {
      stateId: '',
      cityId: '',
      municipalityName: '',
    },
  });
  const municipalityStateId = watchMunicipality('stateId');

  const statesTable = useMemo(
    () => ({
      ...buildTableState(statesList, statesPager),
      isLoading: statesListLoading,
    }),
    [statesList, statesPager, statesListLoading]
  );
  const citiesTable = useMemo(() => citiesData, [citiesData]);
  const municipalitiesTable = useMemo(
    () => municipalitiesData,
    [municipalitiesData]
  );

  const inactiveStates = inactiveRowCount(statesList);
  const inactiveCities = inactiveRowCount(citiesData.data);
  const inactiveMunicipalities = inactiveRowCount(municipalitiesData.data);

  const stateSelectOptions = useMemo(() => {
    const placeholder = { _id: '', name: 'Seleccionar estado...' };
    return [
      placeholder,
      ...statesForDropdown.map((s) => ({
        _id: s._id,
        name: capitalize(s.name),
      })),
    ];
  }, [statesForDropdown]);

  const municipalityCityOptions = useMemo(() => {
    const placeholder = { _id: '', name: 'Seleccionar ciudad' };
    return [
      placeholder,
      ...citiesForMunicipalitySelect.map((c) => ({
        _id: c._id,
        name: capitalize(c.name),
      })),
    ];
  }, [citiesForMunicipalitySelect]);

  const municipalityCityNameDisplay = useMemo(() => {
    if (!editingMunicipalityId) return '';
    const row = municipalitiesData.data.find(
      (m) => m._id === editingMunicipalityId
    );
    if (!row) return '';
    const raw = row.city?.name ?? row.cityName ?? '';
    return raw ? capitalize(String(raw)) : '';
  }, [editingMunicipalityId, municipalitiesData.data]);

  const cancelStateForm = useCallback(() => {
    setEditingStateId(null);
    setShowStateForm(false);
    resetStateForm({ name: '' });
  }, [resetStateForm]);

  const openCreateStateForm = useCallback(() => {
    setEditingStateId(null);
    resetStateForm({ name: '' });
    setShowStateForm(true);
  }, [resetStateForm]);

  const cancelCityForm = useCallback(() => {
    setEditingCityId(null);
    setShowCityForm(false);
    resetCityForm({ stateId: '', cityName: '' });
  }, [resetCityForm]);

  const openCreateCityForm = useCallback(() => {
    setEditingCityId(null);
    resetCityForm({ stateId: '', cityName: '' });
    setShowCityForm(true);
  }, [resetCityForm]);

  const cancelMunicipalityForm = useCallback(() => {
    setEditingMunicipalityId(null);
    setShowMunicipalityForm(false);
    municipalityStateIdPrevRef.current = undefined;
    resetMunicipalityForm({ stateId: '', cityId: '', municipalityName: '' });
  }, [resetMunicipalityForm]);

  const openCreateMunicipalityForm = useCallback(() => {
    setEditingMunicipalityId(null);
    municipalityStateIdPrevRef.current = undefined;
    resetMunicipalityForm({ stateId: '', cityId: '', municipalityName: '' });
    setShowMunicipalityForm(true);
  }, [resetMunicipalityForm]);

  const handleEditState = useCallback(
    (row) => {
      setShowStateForm(true);
      setEditingStateId(row._id);
      resetStateForm({ name: row.name ?? '' });
    },
    [resetStateForm]
  );

  const handleEditCity = useCallback(
    (row) => {
      const stateId =
        row.stateId ??
        (typeof row.state === 'object' && row.state ? row.state._id : row.state) ??
        '';
      setShowCityForm(true);
      setEditingCityId(row._id);
      resetCityForm({
        stateId: stateId || '',
        cityName: row.name ?? '',
      });
    },
    [resetCityForm]
  );

  const handleEditMunicipality = useCallback(
    (row) => {
      const cityId = row.cityId ?? row.city?._id ?? '';
      const stateId =
        row.city?.state?._id ??
        (typeof row.city?.state === 'string' ? row.city.state : '') ??
        row.city?.stateId ??
        '';
      setShowMunicipalityForm(true);
      setEditingMunicipalityId(row._id);
      resetMunicipalityForm({
        stateId: stateId || '',
        cityId: cityId || '',
        municipalityName: row.name ?? '',
      });
    },
    [resetMunicipalityForm]
  );

  const onSubmitStateForm = handleSubmitState(async (data) => {
    const name = data.name.trim();
    if (editingStateId) {
      const row = statesList.find((s) => s._id === editingStateId);
      const response = await statesService.updateState(editingStateId, {
        name,
        isActive: row?.isActive !== false,
      });
      if ([200, 201].includes(response?.status)) {
        notify('Estado actualizado', 'success');
        cancelStateForm();
        setStatesListVersion((v) => v + 1);
      } else {
        notify(`${response.message}` || 'error', 'error');
      }
      return;
    }

    const response = await statesService.createState({ name });
    if ([200, 201].includes(response?.status)) {
      notify('Estado creado', 'success');
      resetStateForm({ name: '' });
      setShowStateForm(false);
      setStatesListVersion((v) => v + 1);
    } else {
      notify(`${response.message}` || 'error', 'error');
    }
  });

  const onSubmitCityForm = handleSubmitCity(async (data) => {
    const name = data.cityName.trim();
    if (editingCityId) {
      const row = citiesData.data.find((c) => c._id === editingCityId);
      const response = await statesService.updateCity(editingCityId, {
        name,
        isActive: row?.isActive !== false,
      });
      if ([200, 201].includes(response?.status)) {
        notify('Ciudad actualizada', 'success');
        cancelCityForm();
        setCitiesListVersion((v) => v + 1);
        setStatesListVersion((v) => v + 1);
      } else {
        notify(`${response.message}` || 'error', 'error');
      }
      return;
    }

    const response = await statesService.createCity({
      name,
      state: data.stateId,
    });
    if ([200, 201].includes(response?.status)) {
      notify('Ciudad creada', 'success');
      resetCityForm({ stateId: '', cityName: '' });
      setShowCityForm(false);
      setCitiesListVersion((v) => v + 1);
      setStatesListVersion((v) => v + 1);
    } else {
      notify(`${response.message}` || 'error', 'error');
    }
  });

  const onSubmitMunicipalityForm = handleSubmitMunicipality(async (data) => {
    const name = data.municipalityName.trim();
    if (editingMunicipalityId) {
      const row = municipalitiesData.data.find(
        (m) => m._id === editingMunicipalityId
      );
      const response = await statesService.updateMunicipality(
        editingMunicipalityId,
        {
          name,
          city: data.cityId,
          isActive: row?.isActive !== false,
        }
      );
      if ([200, 201].includes(response?.status)) {
        notify('Municipio actualizado', 'success');
        cancelMunicipalityForm();
        setMunicipalitiesListVersion((v) => v + 1);
      } else {
        notify(`${response.message}` || 'error', 'error');
      }
      return;
    }

    const response = await statesService.createMunicipality({
      city: data.cityId,
      name,
    });
    if ([200, 201].includes(response?.status)) {
      notify('Municipio creado', 'success');
      resetMunicipalityForm({ stateId: '', cityId: '', municipalityName: '' });
      setShowMunicipalityForm(false);
      setMunicipalitiesListVersion((v) => v + 1);
      setCitiesListVersion((v) => v + 1);
    } else {
      notify(`${response.message}` || 'error', 'error');
    }
  });

  const handleToggleState = useCallback(async (row) => {
    const next = !row.isActive;
    const response = await statesService.updateState(row._id, {
      name: row.name,
      isActive: next,
    });
    if ([200, 201].includes(response?.status)) {
      notify(next ? 'Estado activado' : 'Estado desactivado', 'success');
      setStatesListVersion((v) => v + 1);
    } else {
      notify(`${response.message}` || 'error', 'error');
    }
  }, []);

  const handleToggleCity = useCallback(async (row) => {
    const next = !row.isActive;
    const response = await statesService.updateCity(row._id, {
      name: row.name,
      isActive: next,
    });
    if ([200, 201].includes(response?.status)) {
      notify(next ? 'Ciudad activada' : 'Ciudad desactivada', 'success');
      setCitiesListVersion((v) => v + 1);
    } else {
      notify(`${response.message}` || 'error', 'error');
    }
  }, []);

  const handleToggleMunicipality = useCallback(async (row) => {
    const next = !row.isActive;
    const response = await statesService.updateMunicipality(row._id, {
      name: row.name,
      isActive: next,
    });
    if ([200, 201].includes(response?.status)) {
      notify(next ? 'Municipio activado' : 'Municipio desactivado', 'success');
      setMunicipalitiesListVersion((v) => v + 1);
    } else {
      notify(`${response.message}` || 'error', 'error');
    }
  }, []);

  const handleStatesPageChange = useCallback((_e, page) => {
    setStatesPager((p) => ({ ...p, page }));
  }, []);
  const handleCitiesPageChange = useCallback((_e, page) => {
    setCitiesData((prev) => ({ ...prev, page }));
  }, []);
  const handleMunicipalitiesPageChange = useCallback((_e, page) => {
    setMunicipalitiesData((prev) => ({ ...prev, page }));
  }, []);

  const onSelectPerPage = (setter) => (event) => {
    const value = Number(event.target.value);
    setter({ page: 1, limit: value });
  };

  const onCitiesSelectPerPage = (event) => {
    const value = Number(event.target.value);
    setCitiesData((prev) => ({
      ...prev,
      page: 1,
      limit: value,
    }));
  };

  const onMunicipalitiesSelectPerPage = (event) => {
    const value = Number(event.target.value);
    setMunicipalitiesData((prev) => ({
      ...prev,
      page: 1,
      limit: value,
    }));
  };

  const handleStateIdsFilter = useCallback((ids) => {
    setFilterStatesTabIds(ids);
    setStatesPager((p) => ({ ...p, page: 1 }));
  }, []);

  const geographicStatesHeaderWithFilter = useMemo(
    () =>
      geographicStatesTableHeader.map((h) =>
        h?.value === 'name'
          ? {
              ...h,
              sortable: true,
              filterOptions: statesForDropdown.map((s) => ({
                _id: s._id,
                name: capitalize(s.name),
              })),
              filterSearch: handleStateIdsFilter,
            }
          : h
      ),
    [statesForDropdown, handleStateIdsFilter]
  );

  const handleCitiesStateIdsFilter = useCallback((ids) => {
    setFilterCitiesStateIds(ids);
    setCitiesData((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handleCitiesIdsFilter = useCallback((ids) => {
    setFilterCitiesIds(ids);
    setCitiesData((prev) => ({ ...prev, page: 1 }));
  }, []);

  const geographicCitiesHeaderWithFilter = useMemo(
    () =>
      geographicCitiesTableHeader.map((h) => {
        if (h?.value === 'name') {
          return {
            ...h,
            sortable: true,
            filterOptions: cityFilterOptions,
            filterSearch: handleCitiesIdsFilter,
          };
        }
        if (h?.value === 'belongsTo') {
          return {
            ...h,
            sortable: true,
            filterOptions: statesForDropdown.map((s) => ({
              _id: s._id,
              name: capitalize(s.name),
            })),
            filterSearch: handleCitiesStateIdsFilter,
          };
        }
        return h;
      }),
    [
      cityFilterOptions,
      handleCitiesIdsFilter,
      statesForDropdown,
      handleCitiesStateIdsFilter,
    ]
  );

  const handleMunicipStateIdsFilter = useCallback((ids) => {
    setFilterMunicipStateIds(ids);
    setMunicipalitiesData((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handleMunicipCityIdsFilter = useCallback((ids) => {
    setFilterMunicipCityIds(ids);
    setMunicipalitiesData((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handleMunicipIdsFilter = useCallback((ids) => {
    setFilterMunicipIds(ids);
    setMunicipalitiesData((prev) => ({ ...prev, page: 1 }));
  }, []);

  const geographicMunicipalitiesHeaderWithFilter = useMemo(
    () =>
      geographicMunicipalitiesTableHeader.map((h) => {
        if (h?.value === 'name') {
          return {
            ...h,
            sortable: true,
            filterOptions: municipalityFilterOptions,
            filterSearch: handleMunicipIdsFilter,
          };
        }
        if (h?.value === 'belongsTo') {
          return {
            ...h,
            sortable: true,
            filterOptions: cityOptionsForMunicipFilter,
            filterSearch: handleMunicipCityIdsFilter,
          };
        }
        if (h?.value === 'stateBelongsTo') {
          return {
            ...h,
            sortable: true,
            filterOptions: statesForDropdown.map((s) => ({
              _id: s._id,
              name: capitalize(s.name),
            })),
            filterSearch: handleMunicipStateIdsFilter,
          };
        }
        return h;
      }),
    [
      municipalityFilterOptions,
      handleMunicipIdsFilter,
      cityOptionsForMunicipFilter,
      handleMunicipCityIdsFilter,
      statesForDropdown,
      handleMunicipStateIdsFilter,
    ]
  );

  useEffect(() => {
    if (geoSubTab !== 'estados') {
      setShowStateForm(false);
      setEditingStateId(null);
      resetStateForm({ name: '' });
    }
    if (geoSubTab !== 'ciudades') {
      setShowCityForm(false);
      setEditingCityId(null);
      resetCityForm({ stateId: '', cityName: '' });
    }
    if (geoSubTab !== 'municipios') {
      setShowMunicipalityForm(false);
      setEditingMunicipalityId(null);
      resetMunicipalityForm({ stateId: '', cityId: '', municipalityName: '' });
    }

    // Clear all table filters whenever switching between geographic tabs.
    setFilterStatesTabIds([]);
    setFilterCitiesStateIds([]);
    setFilterCitiesIds([]);
    setFilterMunicipStateIds([]);
    setFilterMunicipCityIds([]);
    setFilterMunicipIds([]);

    // Reset pagination to first page on tab switch.
    setStatesPager((prev) => ({ ...prev, page: 1 }));
    setCitiesData((prev) => ({ ...prev, page: 1 }));
    setMunicipalitiesData((prev) => ({ ...prev, page: 1 }));
  }, [
    geoSubTab,
    resetStateForm,
    resetCityForm,
    resetMunicipalityForm,
  ]);

  useEffect(() => {
    let cancelled = false;
    setStatesListLoading(true);
    Promise.all([
      statesService.listStatesByFilter({ stateIds: filterStatesTabIds }),
      statesService.getStates({ isActive: true }),
    ])
      .then(([fullResponse, activeResponse]) => {
        if (cancelled) return;
        if ([200, 201].includes(fullResponse?.status)) {
          setStatesList(fullResponse?.data?.states ?? []);
        } else {
          notify(fullResponse?.message ?? 'Error al cargar estados', 'error');
          setStatesList([]);
        }
        if (activeResponse.status === 200) {
          setStatesForDropdown(activeResponse?.data?.states ?? []);
        } else {
          setStatesForDropdown([]);
        }
      })
      .catch(() => {
        if (!cancelled) {
          notify('Error al cargar estados', 'error');
          setStatesList([]);
          setStatesForDropdown([]);
        }
      })
      .finally(() => {
        if (!cancelled) setStatesListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [statesListVersion, filterStatesTabIds]);

  useEffect(() => {
    if (geoSubTab !== 'ciudades') return undefined;
    let cancelled = false;
    setCityFilterOptionsLoading(true);
    statesService
      .listCitiesByFilter({ skip: 0, limit: 100000, stateIds: [], citiesIds: [] })
      .then((response) => {
        if (cancelled) return;
        if ([200, 201].includes(response?.status)) {
          setCityFilterOptions(
            (response?.data?.cities ?? []).map((c) => ({
              _id: c._id,
              name: capitalize(c.name ?? ''),
            }))
          );
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setCityFilterOptionsLoading(false); });
    return () => { cancelled = true; };
  }, [geoSubTab, citiesListVersion]);

  useEffect(() => {
    if (geoSubTab !== 'ciudades') {
      return undefined;
    }
    let cancelled = false;
    setCitiesData((prev) => ({ ...prev, isLoading: true }));
    const skip = (citiesData.page - 1) * citiesData.limit;
    statesService
      .listCitiesByFilter({
        skip,
        limit: citiesData.limit,
        stateIds: filterCitiesStateIds,
        citiesIds: filterCitiesIds,
      })
      .then((response) => {
        if (cancelled) return;
        if ([200, 201].includes(response?.status)) {
          setCitiesData((prev) => ({
            ...prev,
            data: response?.data?.cities,
            count: response?.data?.total,
            isLoading: false,
          }));
        } else {
          notify(
            response?.message ??
            response?.data?.message ??
            'Error al cargar ciudades',
            'error'
          );
          setCitiesData((prev) => ({ ...prev, isLoading: false }));
        }
      })
      .catch(() => {
        if (!cancelled) {
          notify('Error al cargar ciudades', 'error');
          setCitiesData((prev) => ({ ...prev, isLoading: false }));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [geoSubTab, citiesData.page, citiesData.limit, citiesListVersion, filterCitiesStateIds, filterCitiesIds]);

  useEffect(() => {
    if (geoSubTab !== 'municipios') return undefined;
    let cancelled = false;
    setCityOptionsForMunicipFilterLoading(true);
    statesService
      .listCitiesByFilter({ skip: 0, limit: 100000, stateIds: [], citiesIds: [] })
      .then((response) => {
        if (cancelled) return;
        if ([200, 201].includes(response?.status)) {
          setCityOptionsForMunicipFilter(
            (response?.data?.cities ?? []).map((c) => ({
              _id: c._id,
              name: capitalize(c.name ?? ''),
            }))
          );
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setCityOptionsForMunicipFilterLoading(false); });
    return () => { cancelled = true; };
  }, [geoSubTab, citiesListVersion]);

  useEffect(() => {
    if (geoSubTab !== 'municipios') return undefined;
    let cancelled = false;
    setMunicipalityFilterOptionsLoading(true);
    statesService
      .listMunicipalitiesByFilter({ skip: 0, limit: 100000, stateIds: [], cityIds: [], municipalityIds: [] })
      .then((response) => {
        if (cancelled) return;
        if ([200, 201].includes(response?.status)) {
          setMunicipalityFilterOptions(
            (response?.data?.municipalities ?? []).map((m) => ({
              _id: m._id,
              name: capitalize(m.name ?? ''),
            }))
          );
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setMunicipalityFilterOptionsLoading(false); });
    return () => { cancelled = true; };
  }, [geoSubTab, municipalitiesListVersion]);

  useEffect(() => {
    if (geoSubTab !== 'municipios') {
      return undefined;
    }
    let cancelled = false;
    setMunicipalitiesData((prev) => ({ ...prev, isLoading: true }));
    const skip = (municipalitiesData.page - 1) * municipalitiesData.limit;
    statesService
      .listMunicipalitiesByFilter({
        skip,
        limit: municipalitiesData.limit,
        stateIds: filterMunicipStateIds,
        cityIds: filterMunicipCityIds,
        municipalityIds: filterMunicipIds,
      })
      .then((response) => {
        if (cancelled) return;
        if ([200, 201].includes(response?.status)) {
          setMunicipalitiesData((prev) => ({
            ...prev,
            data: response?.data?.municipalities ?? [],
            count: response?.data?.total ?? 0,
            isLoading: false,
          }));
        } else {
          notify(
            response?.message ??
            response?.data?.message ??
            'Error al cargar municipios',
            'error'
          );
          setMunicipalitiesData((prev) => ({ ...prev, isLoading: false }));
        }
      })
      .catch(() => {
        if (!cancelled) {
          notify('Error al cargar municipios', 'error');
          setMunicipalitiesData((prev) => ({ ...prev, isLoading: false }));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    geoSubTab,
    municipalitiesData.page,
    municipalitiesData.limit,
    municipalitiesListVersion,
    filterMunicipStateIds,
    filterMunicipCityIds,
    filterMunicipIds,
  ]);

  useEffect(() => {
    if (!showMunicipalityForm || editingMunicipalityId) {
      return undefined;
    }
    const prev = municipalityStateIdPrevRef.current;
    if (prev !== undefined && prev !== municipalityStateId) {
      setMunicipalityValue('cityId', '');
    }
    municipalityStateIdPrevRef.current = municipalityStateId;
    return undefined;
  }, [
    municipalityStateId,
    showMunicipalityForm,
    editingMunicipalityId,
    setMunicipalityValue,
  ]);

  useEffect(() => {
    if (geoSubTab !== 'municipios' || !showMunicipalityForm) {
      setCitiesForMunicipalitySelect([]);
      return undefined;
    }
    if (!municipalityStateId) {
      setCitiesForMunicipalitySelect([]);
      return undefined;
    }

    let cancelled = false;

    statesService
      .getCities(municipalityStateId, true)
      .then((response) => {
        if (cancelled) return;
        if ([200, 201].includes(response?.status)) {
          setCitiesForMunicipalitySelect(response?.data?.cities ?? []);
        } else {
          setCitiesForMunicipalitySelect([]);
        }
      })
      .catch(() => {
        if (!cancelled) setCitiesForMunicipalitySelect([]);
      });
    return () => {
      cancelled = true;
    };
  }, [
    geoSubTab,
    showMunicipalityForm,
    municipalityStateId,
    citiesListVersion,
  ]);

  return (
    <>
      <div className="parameters-page__pills-row">
        <div className="parameters-page__pills">
          <ActionButton
            type="button"
            action={() => setGeoSubTab('estados')}
            label="Estados"
          />
          <ActionButton
            type="button"
            action={() => setGeoSubTab('ciudades')}
            label="Ciudades"
          />
          <ActionButton
            type="button"
            action={() => setGeoSubTab('municipios')}
            label="Municipios"
          />
        </div>
        <ActionButton
          type="button"
          action={() => {
            if (geoSubTab === 'estados') {
              openCreateStateForm();
            } else if (geoSubTab === 'ciudades') {
              openCreateCityForm();
            } else {
              openCreateMunicipalityForm();
            }
          }}
          label={
            geoSubTab === 'estados'
              ? '+ Crear estado'
              : geoSubTab === 'ciudades'
                ? '+ Crear ciudad'
                : '+ Crear municipio'
          }
        />
      </div>

      {((geoSubTab !== 'estados' || showStateForm) &&
        (geoSubTab !== 'ciudades' || showCityForm) &&
        (geoSubTab !== 'municipios' || showMunicipalityForm)) && (
          <div className="parameters-page__create-panel">
            {geoSubTab === 'estados' && showStateForm && (
              <GeographicStateFormPanel
                editingStateId={editingStateId}
                register={registerState}
                errors={stateErrors}
                onSubmit={onSubmitStateForm}
                onCancel={cancelStateForm}
              />
            )}
            {geoSubTab === 'ciudades' && showCityForm && (
              <GeographicCityFormPanel
                editingCityId={editingCityId}
                stateSelectOptions={stateSelectOptions}
                register={registerCity}
                errors={cityErrors}
                onSubmit={onSubmitCityForm}
                onCancel={cancelCityForm}
              />
            )}
            {geoSubTab === 'municipios' && showMunicipalityForm && (
              <GeographicMunicipalityFormPanel
                editingMunicipalityId={editingMunicipalityId}
                cityNameDisplay={municipalityCityNameDisplay}
                stateSelectOptions={stateSelectOptions}
                citySelectOptions={municipalityCityOptions}
                citySelectDisabled={!municipalityStateId}
                register={registerMunicipality}
                errors={municipalityErrors}
                onSubmit={onSubmitMunicipalityForm}
                onCancel={cancelMunicipalityForm}
              />
            )}
          </div>
        )}

      {geoSubTab === 'estados' && (
        <>
          <GeographicStatesTable
            header={geographicStatesHeaderWithFilter}
            data={statesTable}
            handlePageChange={handleStatesPageChange}
            childLabel="ciudades"
            onEdit={handleEditState}
            onToggleActive={handleToggleState}
            itemsPerPageOptions={itemsPerPageOptions}
            onPerPageChange={onSelectPerPage(setStatesPager)}
            inactiveCount={inactiveStates}
          />
        </>
      )}
      {geoSubTab === 'ciudades' && (
        <>
          <GeographicCitiesTable
            header={geographicCitiesHeaderWithFilter}
            data={citiesTable}
            handlePageChange={handleCitiesPageChange}
            childLabel="municipios"
            onEdit={handleEditCity}
            onToggleActive={handleToggleCity}
            itemsPerPageOptions={itemsPerPageOptions}
            onPerPageChange={onCitiesSelectPerPage}
            inactiveCount={inactiveCities}
          />
        </>
      )}
      {geoSubTab === 'municipios' && (
        <>
          <GeographicMunicipalitiesTable
            header={geographicMunicipalitiesHeaderWithFilter}
            data={municipalitiesTable}
            handlePageChange={handleMunicipalitiesPageChange}
            onEdit={handleEditMunicipality}
            onToggleActive={handleToggleMunicipality}
            itemsPerPageOptions={itemsPerPageOptions}
            onPerPageChange={onMunicipalitiesSelectPerPage}
            inactiveCount={inactiveMunicipalities}
          />
        </>
      )}
    </>
  );
};
